// Polaroid frame layouts — shared between FramePreview, InteractiveFrameEditor,
// and the final canvas composition at Photobooth result time.
//
// Both layouts are sized for 10×15 cm print at 300dpi (1200×1800 / 1800×1200 px).
// Thin top/sides, thick bottom (the Polaroid signature caption area).

export const POLAROID_LAYOUTS = {
  polaroid_portrait: {
    id: 'polaroid_portrait',
    label: 'Polaroid Portrait',
    labelHe: 'פולארויד לאורך',
    outputWidth: 1200,
    outputHeight: 1800,
    sideMargin: 60,
    topMargin: 60,
    bottomMargin: 380, // ≈21% — classic Polaroid proportion
  },
  polaroid_landscape: {
    id: 'polaroid_landscape',
    label: 'Polaroid Landscape',
    labelHe: 'פולארויד לרוחב',
    outputWidth: 1800,
    outputHeight: 1200,
    sideMargin: 60,
    topMargin: 60,
    bottomMargin: 260, // ≈22% — same proportion adapted for landscape
  },
};

export function getPolaroidLayout(frameId) {
  return POLAROID_LAYOUTS[frameId] || POLAROID_LAYOUTS.polaroid_portrait;
}

// Size of InteractiveFrameEditor's canvas for a frame of the given shape: 600px
// wide for landscape, 400px otherwise. Text/logo positions are saved in these
// pixels, so the editor and every composition must size from here.
export function getEditorSize(frameWidth, frameHeight) {
  const width = frameWidth > frameHeight ? 600 : 400;
  return { width, height: Math.round(width * (frameHeight / frameWidth)) };
}

// Google Fonts offered for captions in InteractiveFrameEditor (English + Hebrew +
// script families that actually render). The booth composes captions on a page
// where the editor never mounted, so composition loads them as well — canvas
// never waits for a web font and would silently print the fallback instead.
export const FRAME_FONTS_URL =
  'https://fonts.googleapis.com/css2?' +
  'family=Rubik:wght@400;700;900' +
  '&family=Heebo:wght@400;700;900' +
  '&family=Assistant:wght@400;700' +
  '&family=Frank+Ruhl+Libre:wght@500;700' +
  '&family=Suez+One' +
  '&family=Secular+One' +
  '&family=Varela+Round' +
  '&family=Playfair+Display:ital,wght@0,700;1,700' +
  '&family=Dancing+Script:wght@700' +
  '&family=Great+Vibes' +
  '&family=Lobster' +
  '&family=Pacifico' +
  '&family=Caveat:wght@700' +
  '&family=Bebas+Neue' +
  '&family=Oswald:wght@500;700' +
  '&family=Raleway:wght@700' +
  '&family=Montserrat:wght@700;900' +
  '&family=Poppins:wght@600;900' +
  '&display=swap';

let frameFontsStylesheet = null;

// Adds the fonts stylesheet once per page. Resolves when it has loaded — or
// failed, or taken too long: a slow venue network must never hold up the booth.
export function ensureFrameFonts() {
  if (typeof document === 'undefined') return Promise.resolve();
  if (!frameFontsStylesheet) {
    frameFontsStylesheet = new Promise((resolve) => {
      let link = document.getElementById('gf-frame-editor');
      if (link && link.sheet) { resolve(); return; }
      if (!link) {
        link = document.createElement('link');
        link.id = 'gf-frame-editor';
        link.rel = 'stylesheet';
        link.href = FRAME_FONTS_URL;
        document.head.appendChild(link);
      }
      link.addEventListener('load', () => resolve(), { once: true });
      link.addEventListener('error', () => resolve(), { once: true });
      setTimeout(resolve, 4000);
    });
  }
  return frameFontsStylesheet;
}

// Resolves when the promise settles or after ms, whichever comes first.
function withTimeout(promise, ms) {
  return Promise.race([promise, new Promise((resolve) => setTimeout(resolve, ms))]);
}

// Default palette of frame card colors (user can still pick a custom one)
export const POLAROID_COLORS = [
  { value: '#FFFFFF', label: 'White',  labelHe: 'לבן' },
  { value: '#0F0F0F', label: 'Black',  labelHe: 'שחור' },
  { value: '#FFF8E7', label: 'Cream',  labelHe: 'שמנת' },
  { value: '#FFE4E6', label: 'Pink',   labelHe: 'ורוד' },
  { value: '#DBEAFE', label: 'Blue',   labelHe: 'תכלת' },
  { value: '#D1FAE5', label: 'Mint',   labelHe: 'מנטה' },
  { value: '#FEF3C7', label: 'Gold',   labelHe: 'זהב' },
  { value: '#FCA5A5', label: 'Red',    labelHe: 'אדום' },
];

// Bounding box covering every face the browser can find, in source pixels.
// Uses the same FaceDetector API the capture step already relies on; it only
// exists in Chromium, so every caller must cope with null (Safari, Firefox, and
// any image where nothing is found).
async function detectFaceBox(img) {
  try {
    if (typeof window === 'undefined' || !('FaceDetector' in window)) return null;
    const detector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 20 });
    const found = await detector.detect(img);
    if (!found || !found.length) return null;

    let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity;
    for (const f of found) {
      const b = f.boundingBox;
      left = Math.min(left, b.x);
      top = Math.min(top, b.y);
      right = Math.max(right, b.x + b.width);
      bottom = Math.max(bottom, b.y + b.height);
    }
    if (!Number.isFinite(left) || !Number.isFinite(top)) return null;
    return { centerX: (left + right) / 2, top, bottom, count: found.length };
  } catch {
    return null; // detection is an optimisation, never a hard requirement
  }
}

// Compose a polaroid frame onto a base image — used at result/print time.
// Returns a data URL of the composed image.
//
// elements: array of { type:'text'|'logo', x, y, ... } in editor coordinate space
//   (sized by getEditorSize — caller passes editorWidth/editorHeight)
export async function composePolaroidImage({
  baseImageSrc,
  layoutId = 'polaroid_portrait',
  frameColor = '#FFFFFF',
  elements = [],
  editorWidth = 400,
  editorHeight = 550,
}) {
  const layout = getPolaroidLayout(layoutId);
  const { outputWidth: W, outputHeight: H, sideMargin, topMargin, bottomMargin } = layout;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // 1. White (or chosen) polaroid card
  ctx.fillStyle = frameColor;
  ctx.fillRect(0, 0, W, H);

  // 2. Photo area
  const photoX = sideMargin;
  const photoY = topMargin;
  const photoW = W - 2 * sideMargin;
  const photoH = H - topMargin - bottomMargin;

  const baseImg = await loadImage(baseImageSrc);
  await drawImageCover(ctx, baseImg, photoX, photoY, photoW, photoH);

  // Subtle inner shadow on photo edges
  ctx.strokeStyle = 'rgba(0,0,0,0.08)';
  ctx.lineWidth = 2;
  ctx.strokeRect(photoX, photoY, photoW, photoH);

  // 3. Render user-placed elements from the editor
  await drawFrameElements(ctx, elements, { width: W, height: H, editorWidth, editorHeight, frameColor });

  return canvas.toDataURL('image/jpeg', 0.95);
}

// Fill a rectangle of the canvas with an image, cropped to the rectangle's shape.
//
// The generated image rarely matches the frame's window. seedream-pro in
// particular ALWAYS returns 2048x2048 no matter what aspect we ask for (size,
// aspect_ratio, width/height and size=4:5 were all tested and ignored), so a
// square has to become a 4:5 portrait here. Cropping from the geometric centre
// cuts ~20% off each side of a square, which is exactly where people at the
// edges of a group stand — so aim the crop at the faces when the browser can
// find them.
export async function drawImageCover(ctx, img, x, y, w, h) {
  const photoRatio = w / h;
  const imgRatio = img.width / img.height;

  // Largest region of the source with the frame's aspect ratio.
  let sw, sh;
  if (imgRatio > photoRatio) { sh = img.height; sw = sh * photoRatio; }
  else                       { sw = img.width;  sh = sw / photoRatio; }

  const faces = await detectFaceBox(img);
  let sx, sy;
  if (faces) {
    sx = faces.centerX - sw / 2;
    // Put the top of the faces about a fifth of the way down the crop: heads
    // stay clear of the edge and the composition keeps the eyes high.
    sy = faces.top - sh * 0.18;
  } else {
    sx = (img.width - sw) / 2;
    // Without detection, take 30% of the excess off the top and 70% off the
    // bottom — losing empty floor beats chopping off heads.
    sy = (img.height - sh) * 0.3;
  }
  sx = Math.max(0, Math.min(sx, img.width - sw));
  sy = Math.max(0, Math.min(sy, img.height - sh));

  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
  ctx.restore();
}

// Draw the editor's text and logo elements, scaled from editor coordinates to
// the output canvas.
export async function drawFrameElements(ctx, elements, { width, height, editorWidth, editorHeight, frameColor }) {
  const scaleX = width / editorWidth;
  const scaleY = height / editorHeight;

  for (const el of elements) {
    const x = el.x * scaleX;
    const y = el.y * scaleY;

    if (el.type === 'text' && el.content) {
      const fontSize = (el.fontSize || 22) * scaleY;
      const font = `bold ${fontSize}px ${el.fontFamily || 'Arial'}, sans-serif`;
      // Fetch this face — for these characters, since Hebrew is its own subset —
      // before drawing; see FRAME_FONTS_URL.
      await ensureFrameFonts();
      try {
        await withTimeout(document.fonts.load(font, el.content), 3000);
      } catch { /* unparseable font string — draw with whatever resolves */ }
      ctx.font = font;
      ctx.fillStyle = el.color || (isLightHex(frameColor) ? '#1a1a1a' : '#FFFFFF');
      ctx.textBaseline = 'top';
      ctx.shadowColor = 'rgba(0,0,0,0.25)';
      ctx.shadowBlur = 2;
      ctx.shadowOffsetY = 1;
      ctx.fillText(el.content, x, y);
      ctx.shadowColor = 'transparent';
    } else if (el.type === 'logo' && el.src) {
      try {
        const logoImg = await loadImage(el.src);
        const lw = (el.width || 80) * scaleX;
        const lh = (el.height || 50) * scaleY;
        ctx.drawImage(logoImg, x, y, lw, lh);
      } catch { /* skip broken logo */ }
    }
  }
}

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function isLightHex(hex) {
  if (!hex) return true;
  const h = hex.replace('#', '');
  if (h.length !== 6) return true;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) > 0.6;
}
